import { type } from 'arktype';

export const personLocationTypeSchema = type("'primary' | 'secondary' | 'home_office'").configure({
  description:
    "Type of person-location assignment: 'primary' is the main work location, 'secondary' is a regular alternative site, and 'home_office' indicates remote/home working.",
});

export const personLocationQuerySchema = type({
  'personId?': type('string').configure({
    description: 'Filter assignments by person ID. Optional.',
    examples: ['per_abc123'],
  }),
  'locationId?': type('string').configure({
    description: 'Filter assignments by location ID. Optional.',
    examples: ['loc_hq01'],
  }),
  'type?': personLocationTypeSchema,
  'startDate?': type('string').configure({
    description: 'Filter assignments whose validity starts on this ISO date (YYYY-MM-DD). Optional.',
    examples: ['2026-04-22'],
  }),
  'endDate?': type('string').configure({
    description: 'Filter assignments whose validity ends on this ISO date (YYYY-MM-DD). Optional.',
    examples: ['2026-12-31'],
  }),
}).configure({ description: 'Query parameters for filtering person-location assignments.' });

export type PersonLocationQuery = typeof personLocationQuerySchema.t;

export const createPersonLocationSchema = type({
  personId: type('string').configure({
    description: 'ID of the person being assigned to the location.',
    examples: ['per_abc123'],
  }),
  locationId: type('string').configure({
    description: 'ID of the location the person is being assigned to.',
    examples: ['loc_hq01'],
  }),
  type: personLocationTypeSchema,
  'startDate?': type('string | null | undefined').configure({
    description: 'ISO date (YYYY-MM-DD) when the assignment starts. Optional and nullable (open-ended start).',
    examples: ['2026-04-22'],
  }),
  'endDate?': type('string | null | undefined').configure({
    description: 'ISO date (YYYY-MM-DD) when the assignment ends. Optional and nullable (open-ended end).',
    examples: ['2026-12-31'],
  }),
}).configure({ description: 'Payload to assign a person to a location with an optional validity period.' });

export type CreatePersonLocation = typeof createPersonLocationSchema.t;

export const updatePersonLocationSchema = type({
  'personId?': type('string').configure({
    description: 'Updated person ID. Optional — rarely changed after creation.',
    examples: ['per_abc123'],
  }),
  'locationId?': type('string').configure({
    description: 'Updated location ID. Optional.',
    examples: ['loc_hq01'],
  }),
  'type?': personLocationTypeSchema,
  'startDate?': type('string | null | undefined').configure({
    description: 'Updated start date (ISO YYYY-MM-DD). Optional and nullable.',
    examples: ['2026-04-22'],
  }),
  'endDate?': type('string | null | undefined').configure({
    description: 'Updated end date (ISO YYYY-MM-DD). Optional and nullable.',
    examples: ['2026-12-31'],
  }),
}).configure({ description: 'Partial update payload for an existing person-location assignment.' });

export type UpdatePersonLocation = typeof updatePersonLocationSchema.t;

export interface PersonLocationAssignmentResponse {
  id: string;
  personId: string;
  locationId: string;
  type: 'primary' | 'secondary' | 'home_office';
  startDate: string | null;
  endDate: string | null;
  createdAt: string;
  updatedAt: string;
  source: 'direct' | 'inherited';
  inheritedFrom?: { teamId: string; teamName: string };
  effectiveStartDate?: string | null;
  effectiveEndDate?: string | null;
}
